/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { reactive } from 'vue';
import { mapState, mapActions } from 'vuex';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import diagnoseResource from '../../../../share/util/diagnose';
import projectData from '../../../../share/components/vue/ProjectData';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import cstmTable from '../../../../share/components/vue/PaginationTable';
import noTextSVGButton, { ICON_KEY, BUTTON_TYPE } from "../../../../share/components/vue/NoTextSVGButton";
import ffdModal from '../../../vue/FFDModal';
import DataMonitor from './Requirement045DataMonitor';
import dataMonitorMeasure from '../../../vue/dataMonitor/DataMonitorMeasure';
import WS45Dialog from './Requirement045Dialog';
import judgeAvailability from './api/JudgeAvailability';
import judgeReadinessCode from './api/JudgeReadinessCode';
import getDriveCycleData from './api/GetDriveCycleData';
import memoryClear from './api/MemoryClear';
import startDataMonitor from './api/StartDataMonitor';
import stopDataMonitor from './api/StopDataMonitor';
import showResult from './api/ShowResult';
import defaultSessionTransition from './api/DefaultSessionTransition';
import requirement045Utils from './api/Requirement045Utils';

export default {
  name: 'requirement045',
  data() {
    return {
      dialog: null,
      isLoading: false,
      isStartToggle: false, // データモニタ画面「開始」ボタントグル
      isEndToggle: true,    // データモニタ画面「終了」ボタントグル
      systemId: '',
      obdDataId: '',
      systemNo: null,
      projectId: '',
      driveCycleDataResult: '',
      isNonDefaultSession: false, // セッションフラグ
      isMonitorStarted: false,    // MonitorAPI既実行フラグ
      isMeasureStated: false,     // データモニタ計測データ取得API既実行フラグ
      measuredId: '',
      standbyOnboardErrorCode: '',
      standbyOnboardMeasuredId: '',
      showResultOnboardErrorCode: '',
      showResultOnboardMeasuredId: '',
      dtcMeasuredId: '',
      isInterruption: false,      // データモニタ中断中フラグ
      dtcStatusAvailabilityMask: null,
      getMonitorTestDataResponse: {},
      getDiagMeasuredDataResponse: {},
      monitoringInterval: null,
      timestamp: null,
      displayTableColumns: [],
      displayTableInfo: [],
      dataMonitorTemporaryResponse: {},
      dataMonitorPhase: '',       // データモニタ中断フェーズ
      isMonitorTestInitial: true, // モニタテスト初回実行フラグ
      evapoLeakTestResult: '',
      isDiagInitial: true,        // DTC取得初回実行フラグ
      onboardMonitorTest: [],
      readDtcList: [],
      cachedFFDs: [], // 取得済みFFD
      ffdTitle: '',
      ffdSystem: '',
      ffdDescription: '',
      selectedSystem: '',
      ffdSystemTable: [],
      readinessCodeList: [],
      vin: '',                             // データ保存画面のVIN/車両番号
      vehicleName: '',                     // データ保存画面の車両
      displayProjectID: '',                // データ保存画面のID
      hideRegistrationNumber: true,        // データ保存画面の登録番号表示フラグ
      registrationNumber: '',              // データ保存画面の登録番号表示
      isAlert: {},                         // データ保存画面の入力制限オブジェクト
      diagnoseComment: '',                 // データ保存画面の診断コメント
      functionName: 'individual.individual_diagnosis_function_work_support', // データ保存画面の機能名
      dataName: '',                        // データ保存画面のデータ名
      memo: '',                            // データ保存画面のメモ
      modal_requirement_045: false,
      dataMonitor: null, // DMインスタンス
      currentSortkey: '',  // テーブル表示画面で使用
      errorResult: null, // エラー情報一時保存用変数
      ICON_KEY: ICON_KEY, // SVGアイコンの種類
      BUTTON_TYPE: BUTTON_TYPE, // SVGアイコンのボタンの種類
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
    additionalInfo: { type: Object, required: true }
  },

  components: {
    'data-monitor-measure': dataMonitorMeasure,
    'cstm-table': cstmTable,
    'project-data': projectData,
    'ffd-modal': ffdModal,
    'no-text-svg-button': noTextSVGButton,
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      vehicleInfo: state => state.cache.vehicleInfo,
      individualMenus: state => state.vehicle.individualMenus,
      vehicleModelDisplayNameList: state => state.masterResources.vehicleModelDisplayNameList,
      language: state => state.systems.language,
    }),

    /** ダイアログのヘッダー色 */
    headerColor() { return this.dialog.headerColor(); },
    /** ダイアログのヘッダタイトル */
    headerTitle() { return this.dialog.headerTitle(); },
    /** 全画面共通 表示文言 */
    displayInfo() { return this.dialog.displayInfo(); },
    /** 各ボタン表示有無 */
    showCancelButton() { return this.dialog.showButton("Cancel"); },
    showOkButton() { return this.dialog.showButton("Ok"); },
    showNoButton() { return this.dialog.showButton("No"); },
    showYesButton() { return this.dialog.showButton("Yes"); },
    showStartButton() { return this.dialog.showButton("Start"); },
    showFinishButton() { return this.dialog.showButton("Finish"); },
    showSaveButton() { return this.dialog.showButton("Save"); },
    /** モーダルサイズ */
    modalSize() { return this.dialog.modalSize(); },

    // データモニタ画面 グラフ設定ボタン押下可否
    isGraphConfigButtonDisable() { return !this.dataMonitor.checkLines; },

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession', "stopSessionTimer", "resetSessionTimer"]),
    ...mapActions('app', ['setIsTimeoutLocked']),

    /**
     * 共通エラーへ遷移
     * @param {boolean} loadingOff falseならばスピナーOFFしない
     */
    showUnexpectedError(loadingOff = true) {
      if (loadingOff) { this.isLoading = false; }
      this.dialog.phase = WS45Dialog.PHASE.FAILED;
    },

    /**
     * 通信エラーへ遷移
     * @param {boolean} loadingOff falseならばスピナーOFFしない
     */
    showCommunicationError(loadingOff) {
      if (loadingOff) { this.isLoading = false; }
      this.dialog.phase = WS45Dialog.PHASE.COMMUNICATION_ERROR;
    },

    /**
    * API 通信で共通バリデーションでエラーが発生した際のコールバック
    * @param {object} result handler.validate で返ってきたバリデーション結果
    * @param {object} isImmediate 即時実行フラグ
    */
    async validateErrorResult(result, isImmediate = true) {

      // 即時実行しない場合はエラー処理を実行せず結果のみを保持して処理終了
      if (!isImmediate) {
        this.errorResult = this.errorResult ? this.errorResult : result;
        return;
      }

      // 例外がスローされた場合は exceptionHandler を実施して処理終了
      if (result.causeType === "callAPIExceptionHandler") {
        await requirement045Utils.exceptionHandler(...result.exceptionHandlerArgs);
        return;
      }

      // 上記以外はデフォルトのエラーハンドリング処理を実行
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        () => { this.logout(this); },
        // セッションリセット処理のコールバック
        () => { this.resetSession(this); },
        // その他エラー発生のコールバック
        () => { this.showUnexpectedError(true); },
        // 注意 `...mapActions('auth', ['logout', 'resetSession']),` の追加が必要です。
      );
      this.$nextTick(() => {
        this.isLoading = false;
      });
      // エラー処理実行後はエラー結果保持変数を初期化
      this.errorResult = null;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_045 = false;
    },

    /** 初期化処理 */
    init() {
      // WS.45モーダル呼び出し
      this.modal_requirement_045 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // dataMonitorインスタンスにsystemIdを渡す
      this.dataMonitor.specifiedSystemId = this.systemId;
      // DM画面マークボタン活性化
      this.dataMonitor.measurementUuid = "-";
      // リソース(作動履歴_DID)のi18nキー取得
      this.i18nWsDidKey = `wsDid${this.systemId.toUpperCase()}`;
      // プロジェクトIDの取得
      this.projectId = this.vehicleInfo ? this.vehicleInfo.vehicle.project.id : null;
      // dataMonitorインスタンス生成時に''(空)で渡されていたプロジェクトIDを更新
      this.dataMonitor.ws45MarkerInfo.projectId = this.projectId;
      // 実行可否確認
      this.judgeObdCommunicationStandard();
    },

    /** キャンセルボタン押下時処理 */
    onCancelClicked() { this.dialog.onCancelClicked(); },

    /** OKボタン押下時処理 */
    onOkClicked() { this.dialog.onOkClicked(); },

    //----- 【待機】実行可否確認 ------------------------------------------------------//
    /** [obd] OBD通信規格判定 */
    judgeObdCommunicationStandard() {
      judgeAvailability.judgeObdCommunicationStandard(this);
    },
    /** [obd] 初期化通信 */
    async initCommunication() {
      await judgeAvailability.initCommunication(this);
    },
    /** サポート読出し結果取得 */
    async getSupportMenu() {
      await judgeAvailability.getSupportMenu(this);
    },
    /** サポートID取得 */
    async getSupportIdsProcess() {
      await judgeAvailability.getSupportIdsProcess(this);
    },
    /** 
     * [obd] サポートID取得API実行 
     * @return {array} サポートIDリスト
     */
    async getSupportIds() {
      return await judgeAvailability.getSupportIds(this);
    },

    //----- 【待機】レディネスコード取得 ------------------------------------------------------//
    /** [diag] レディネスコード判定 */
    async judgeReadinessCode() {
      await judgeReadinessCode.judgeReadinessCode(this);
    },
    /** [diag] レディネスコード判定結果取得処理 */
    async getReadinessCodeJudgeResult() {
      await judgeReadinessCode.getReadinessCodeJudgeResult(this);
    },

    //----- 【待機】ドライブサイクルデータ取得 -----------------------------------------------------//
    /** [wsp] Read ドライブサイクルデータ取得 */
    async readDriveCycleData() {
      await getDriveCycleData.readDriveCycleData(this);
    },
    /** ドライブサイクルデータ取得画面 OKボタン押下 : ダイアグコード消去画面へ遷移 */
    diagCodeDeleteProcess() {
      this.dialog.phase = WS45Dialog.PHASE.DIAG_CODE_DELETE;
    },
    /** ドライブサイクルデータ取得/ダイアグコード消去 キャンセルボタン押下 : 中断表示画面へ遷移*/
    interruptionProcess() {
      this.dialog.phase = WS45Dialog.PHASE.INTERRUPTION;
    },
    /** ドライブサイクルデータ取得結果表示内容を整形 */
    async makeDriveCycleDataDisplayInfo(response) {
      return await getDriveCycleData.makeDriveCycleDataDisplayInfo(this, response);
    },

    //----- 【待機】ダイアグコード消去 ------------------------------------------------------//
    /** [obd] メモリクリア 
     *  [diag]メモリクリア
    */
    async MemoryClear() {
      await memoryClear.memoryClear(this);
    },

    /**[diag] メモリクリアGet処理 */
    async getMemoryClearResult() {
      await memoryClear.getMemoryClearResult(this);
    },

    //----- データモニタ表示 -----------------------------------------------------//
    /** データモニタ画面の表示 */
    showDataMonitor() {
      this.dialog.phase = WS45Dialog.PHASE.DATA_MONITOR;
    },
    /** データモニタ中断処理 */
    interruptDataMonitor() {
      this.dialog.phase = WS45Dialog.PHASE.DATA_MONITOR_INTERRUPTION;
      this.isInterruption = true;
      // 中断画面に遷移する際に getMeasuredData の実行が完了するまでボタン操作を受け付けないようにするために設定する
      this.isLoading = true;
    },
    /** データモニタ開始(再開)処理 */
    async startDataMonitor() {
      this.dialog.phase = WS45Dialog.PHASE.DATA_MONITOR;
      this.isInterruption = false;
      // 中断フェイズから処理を再開
      if (this.dataMonitorPhase === 'handleGetMeasuredData') {
        this.dialog.phase = WS45Dialog.PHASE.DATA_MONITOR_STARTED;
        await this.getMeasuredData();
      } else {
        // 初回：データモニタ処理開始
        await this.obdOnboardSystemTest();
      }
    },
    /** [obd] オンボードシステムテスト */
    async obdOnboardSystemTest() {
      await startDataMonitor.obdOnboardSystemTest(this);
    },
    /** [obd] オンボードモニタテスト */
    async standbyObdOnboardMonitorTest() {
      await startDataMonitor.standbyObdOnboardMonitorTest(this);
    },
    /** [wsp] Standby 準備処理 */
    async initDataMonitor() {
      await startDataMonitor.initDataMonitor(this);
    },
    /** [wsp] Monitor モニター */
    async monitor() {
      await startDataMonitor.monitor(this);
    },
    /** [wsp] 計測データ取得 */
    async getMeasuredData() {
      await startDataMonitor.getMeasuredData(this);
    },
    /** [wsp] 計測データ取得(レスポンスに対する処理) */
    async handleGetMeasuredData() {
      await startDataMonitor.handleGetMeasuredData(this);
    },

    //----- データモニタ終了 ------------------------------------------------------//
    /** データモニタ中断表示にて[OK]押下 */
    async terminateDataMonitor() {
      // 変数初期化
      this.dataMonitorPhase = '';
      this.dataMonitorTemporaryResponse = null;
      this.isStartToggle = false;
      this.isEndToggle = true;
      // データモニタ停止処理へ遷移
      const stopMonitorResult = await this.stopMonitor(false, false);
      // 計測データ削除へ遷移
      const clearMeasuredDataResult = await this.clearMeasuredData(false, false);
      // 終了処理へ遷移
      const sessionTransitionResult = await this.defaultSessionProcess(0, 1, true, false);
      if (stopMonitorResult && clearMeasuredDataResult && sessionTransitionResult) {
        // 結果がすべて正常終了であれば、データモニタ表示画面の表示
        this.displayTableColumns = this.displayTableInfo = [];
        this.dialog.phase = WS45Dialog.PHASE.DATA_MONITOR;
      } else {
        this.validateErrorResult(this.errorResult);
      }
    },
    /**
     * [wsp] データモニタ停止
     * @param {boolean} loadingOff falseならばスピナーOFFしない
     * @param {boolean} isImmediate エラー即時実行フラグ
     */
    async stopMonitor(loadingOff, isImmediate) {
      return await stopDataMonitor.stopMonitor(this, loadingOff, isImmediate);
    },
    /** [wsp] 計測データ削除
     * @param {boolean} loadingOff falseならば成功時にスピナーOFFしない
     * @param {boolean} isImmediate エラー即時実行フラグ
     */
    async clearMeasuredData(loadingOff, isImmediate) {
      return await stopDataMonitor.clearMeasuredData(this, loadingOff, isImmediate);
    },
    /** エバポリークテスト結果表示 OKボタン押下 */
    async exitDataMonitor() {
      const stopMonitorResult = await this.stopMonitor(false, false);
      const sessionTransitionResult = await this.defaultSessionProcess(0, 1, false, false);
      // オンボードモニタテストへ遷移
      if (sessionTransitionResult && stopMonitorResult) {
        await showResult.getOnboardMonitorTest(this);
      } else {
        await this.clearMeasuredData(false, false);
        this.validateErrorResult(this.errorResult);
      }
    },

    //----- 結果取得・表示 -----------------------------------------------------//
    /** [diag] ダイアグコード取得 */
    async getDiagCode() {
      await showResult.getDiagCode(this);
    },
    /** プロジェクトデータ保存画面 表示情報取得 */
    screenMovement() {
      showResult.screenMovement(this);
    },
    /** プロジェクトデータ保存画面 [保存]ボタン押下 */
    async callSaveEvent() {
      // 入力制限チェック
      if (await this.$refs.projectData.validate()) {
        // [wsp] プロジェクトデータ保存処理
        await this.update();
      }
      this.isLoading = false;
    },
    /** [wsp] プロジェクトデータ保存 */
    async update() {
      await showResult.update(this);
    },
    /** プロジェクトデータ保存画面 [キャンセル]ボタン押下 */
    async callSaveCancelled() {
      // 計測データ削除へ遷移
      if (await this.clearMeasuredData(false, true)) {
        // [wsp] オンボードモニタテスト結果取得へ遷移
        await this.getMonitorTestData();
      }
    },
    /** [wsp] オンボードモニタテスト結果取得 */
    async getMonitorTestData() {
      await showResult.getMonitorTestData(this);
    },
    /** [diag] 計測データ(DTC)取得 */
    async getDiagMeasuredData() {
      await showResult.getDiagMeasuredData(this);
    },
    /**
     * DTC表示画面 FFDボタン押下時処理
     * @param {object} data DTC表示FFDボタン押下行のレコード情報
     */
    async rowButtonClickEvent(data) {
      await showResult.rowButtonClickEvent(this, data);
    },
    /** DTC表示画面 レディネスコードボタン押下 */
    async getReadinessMeasuredData() {
      // 初回以降 : レディネスコード表示画面へ遷移
      this.dialog.phase = WS45Dialog.PHASE.READINESS_CODE_DISPLAY;
      // レディネスコード結果一覧作成処理
      showResult.readinessCodeTableDataConvert(this, this.getDiagMeasuredDataResponse.data.data);
    },
    /** モニタテスト結果表示 終了ボタン押下 */
    completionProcess() {
      // 完了表示画面へ遷移
      this.dialog.phase = WS45Dialog.PHASE.COMPLETION;
    },

    //----- DefaultSession遷移 -----------------------------------------------------//
    /**
     * DefaultSession遷移処理
     * @param {*} existence 0:エラー遷移あり, 1:エラー遷移なし
     * @param {*} transitionDestination 0:機能終了,1:呼び出し元の次の処理を実行
     * @param {boolean} loadingOff falseならばスピナーOFFしない
     * @param {boolean} isImmediate エラー即時実行フラグ
     * @returns {boolean} 成否
     */
    async defaultSessionProcess(existence, transitionDestination, loadingOff, isImmediate) {
      return await defaultSessionTransition.defaultSessionProcess(this, existence, transitionDestination, loadingOff, isImmediate);
    },
    // ----- DM関連処理 -----------------------------------------------------//
    /** 
     * グラフ項目件数を変更
     * @param {number} checkLines チェック数
     */
    changeCheckValue(checkLines) {
      this.dataMonitor.checkLines = checkLines;
    },
    /** 選択項目の言語変更データ受取 */
    changeSelectedFuncs(list) {
      const selectedFuncsHash = {};
      for (const selectedFunction of list) {
        const { data_monitor_id, ...conv } = selectedFunction;
        selectedFuncsHash[data_monitor_id] = conv;
      }
      this.dataMonitor.selectedFuncs = list;
      this.dataMonitor.selectedFunctionHash = selectedFuncsHash;
    },
    /** 
     * グラフ設定情報を保持
     * @param {number} xRange 横軸設定
     * @param {object} yRange 縦軸設定
     * @param {object} lineSetting ライン設定
     */
    setGraphConfig(xRange, yRange, lineSetting) {
      this.dataMonitor.graphConfigInfo.xRange = xRange;
      // yRangeに項目が存在していれば更新、存在しなければ追加、設定済みの項目はそのまま
      Object.keys(yRange).forEach((did) => { this.dataMonitor.graphConfigInfo.yRange[did] = yRange[did]; });

      // lineSettingに項目が存在していれば更新、存在しなければ追加、設定済みの項目はそのまま
      if (lineSetting) {
        Object.keys(lineSetting).forEach((did) => {
          this.dataMonitor.graphConfigInfo.lineSetting[did] = lineSetting[did];
        });
      }
    },
    // グラフ設定
    handleConfigureGraph() {
      this.$refs.data_monitor_measure.handleConfigureGraph();
    },
  },

  created() {
    // ダイアログインスタンス生成
    this.dialog = reactive(new WS45Dialog.Dialog(this));
    // DMインスタンス生成
    this.dataMonitor = reactive(new DataMonitor(this, "measurement"));
  },

  async mounted() {
    this.init();
  },
};